import { Alert, Button, Card, Form, ListGroup, Modal, Spinner } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';

import { Box } from '@material-ui/core';
import type { AxiosResponse } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import { MdSettings } from 'react-icons/md';
import { RiCloseCircleFill } from 'react-icons/ri';
import CancelModal from '../../Components/CancelModal';
import { useStateValue } from '../../providers/StateProvider';
import { deleteField, getFields, postField, updateField } from '../../services/field';
import { actionTypes } from '../../store/reducer';
import { getMaxNumberInData } from '../../utils/getMaxNumberInData';
import { CardHeader, Title } from '../Flow/components/ManageFlow/styles';
import { CardIcon, ContainerMain } from '../Flow/styles';
import { AddressList } from './AddressList';
import { Conditional } from './Conditional';
import { ButtonHeader, CustomAlert, FormLabelCustom, InfoIcon } from './styles';
import { schema } from './validation';

export type FieldType = {
  id: string
  nome: string
  tipo: string
  resposta: string
  texto_ajuda: string
  opcoes: Array<{
    id: string
    resposta: string
  }>
}

export type ConditionalOptionType = {
  id: string
  resposta: string
  duplicar_campos: number
  texto_ajuda: string
  lista_campos: FieldType[]
}

export type ConditionalType = {
  id: string
  pergunta: string
  tipo: string
  texto_ajuda: string
  opcoes: ConditionalOptionType[]
}

export type RelatedFields = DataField

export type DataField = {
  id?: number,
  ordem: string,
  titulo: string,
  texto_interno: string,
  formulario: string,
  mascara: number | string,
  validador: string,
  tipo: string,
  texto_ajuda: string,
  obrigatorio: boolean,
  condicional_campos?: ConditionalType[]
  alternativas: any
  tipo_alternativa?: string,
  limite_replicacoes?: number
  campo?: string
  campos_relacionados?: Array<RelatedFields>
}

type AlternativesTypeProp = 'Matriz' | 'Alternativas' | 'Multiplos' | 'Multiplos_API' | 'Alternativas_API';
interface AlternativesProp {
  ordem: string;
  titulo: string;
  campo?: string;
  valor?: string;
}

type EmptyField = string[]

const Build = () => {
  const history = useHistory();
  const { form_slug, flow_slug } = useParams<{ form_slug: string, flow_slug: string }>();
  const [tipoCampo, setTipoCampo] = useState('Texto');
  const [, setShowOptionsOne] = useState(false);
  const [showOptionsTwo, setShowOptionsTwo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emptyFields, setEmptyFields] = useState<EmptyField>([])

  const [, dispatch] = useStateValue();

  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);


  const intialState: DataField = {
    ordem: '1',
    titulo: '',
    texto_interno: '',
    formulario: form_slug,
    mascara: '',
    validador: '',
    tipo: 'Texto',
    texto_ajuda: '',
    obrigatorio: true,
    alternativas: [],
    condicional_campos: [],
    limite_replicacoes: 0,
  }

  const [formData, setFormData] = useState<DataField>(intialState);
  const [formsData, setFormsData] = useState<Array<DataField>>([]);
  const [showModalCancel, setShowModalCancel] = useState(false);
  const [errorsFieldRequired, setErrorsFieldRequired] = useState<any | undefined>();
  const [isConditionalValid, setIsConditionalValid] = useState(false)
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const addressFieldAdded = useMemo(() =>{
    const addressFieldsOrder = ['Estado', 'Cidade', 'Bairro', 'Rua/Avenida']

    return addressFieldsOrder.filter((field) => formData?.campos_relacionados?.some((item) => item.titulo === field))
  } ,[formData])


  const cepFormData = useMemo(() => formsData.find((form) => {
    const isCep = form.titulo === 'CEP' && form.tipo === 'CEP'

    return form?.id === selectedId && isCep
  }), [formsData, selectedId])


  
  const handleRemoveAddressFields = useCallback(async () => {
    const fieldsDelete = cepFormData?.campos_relacionados?.map((field) => field.titulo)

    if(fieldsDelete?.length === 0) return

    try {
       await Promise.all([
        ...cepFormData?.campos_relacionados?.map((field) =>  {
          return deleteField(field.id, form_slug)
        })
      ])

      setTimeout(() => {
        dispatch({
          type: actionTypes.SET_FLASH_MESSAGE,
          flashMessage: {
            show: true,
            type: 'success',
            title: 'Sucesso',
            message: `Foram apagados os campos: ${fieldsDelete.join(', ')} junto com o campo CEP`,
          }
        })

      }, 1000)

    } catch(err){
      console.log(err)

      setTimeout(() => {
        dispatch({
          type: actionTypes.SET_FLASH_MESSAGE,
          flashMessage: {
            show: true,
            type: 'error-alt',
            title: 'Error',
            message: `Não foi possivel apagar os campos: ${fieldsDelete.join(', ')}`,
          }
        })
      }, 1000);
    }
  
  }, [cepFormData, form_slug])

 

  const handleRemove = async (id: number) => {
    try {
      setLoading(true);

      await deleteField(id, form_slug);


      if(cepFormData?.id){
        await handleRemoveAddressFields()
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
    await getAllFields();
    setShowModalCancel(false);

  }

  const [showModalNewInput, setShowModalNewInput] = useState(false);
  const handleCloseModalNewInput = () => {
    const getMaxOrder = formsData && getMaxNumberInData<DataField>(formsData, 'ordem');
    intialState.ordem = (getMaxOrder + 1).toString();
    setFormData(intialState);
    setSelectedId(undefined);
    setShowModalNewInput(false);
    setTipoCampo('Texto');
  };
  const handleShowModalNewInput = () => setShowModalNewInput(true);

  const getAllFields = async () => {
    try {
      setLoading(true);
      const { data } = await getFields(form_slug);
      if (data.length) {
        setFormsData(data);
      }
    } catch (error) {
      console.log(error);

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form_slug]);

  useEffect(() => {
    if (formsData && formsData.length > 0 && !selectedId) {
      let getMaxOrder = getMaxNumberInData<DataField>(formsData, 'ordem');
      setFormData({
        ...formData,
        ordem: (getMaxOrder + 1).toString(),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formsData, selectedId]);

  useEffect(() => {
    if (tipoCampo === 'Texto'
      || tipoCampo === 'Numero'
      || tipoCampo === 'Data'
      || tipoCampo === 'Area'
      || tipoCampo === 'Telefone'
      || tipoCampo === 'Arquivo'
      || tipoCampo === 'Email') {
      setShowOptionsOne(true);
      setShowOptionsTwo(false);
    } else if (tipoCampo === 'Alternativas'
      || tipoCampo === 'Alternativas_API'
      || tipoCampo === 'Matriz'
      || formData.tipo === 'Condicional'
      || tipoCampo === 'Multiplos'
      || tipoCampo === 'Multiplos_API'
      || tipoCampo === 'Multiplos_Arquivos') {

      if (tipoCampo === 'Matriz' || formData.tipo === 'Condicional') {
        setFormData({ ...formData, tipo_alternativa: "Campo" })

        if (formData.tipo === 'Condicional') {
          setFormData(state => ({ ...state }))
        }
      }
      setShowOptionsOne(false);
      setShowOptionsTwo(true);
    } else {
      setShowOptionsOne(false);
      setShowOptionsTwo(false);
    }
    if (tipoCampo === 'Alternativas' && !formData.tipo_alternativa) {
      setFormData({
        ...formData,
        tipo_alternativa: 'Select'
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCampo]);

  const getAlternativas = () => {
    if (typeof formData.alternativas === 'string') {
      return JSON.parse(formData.alternativas);
    } else {
      return formData.alternativas;
    }
  }

  const checkValidJsonObject = (jsonString: string) => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (error) {
      return false;
    }
  }

  const getErrorsInAlternatives = (type: AlternativesTypeProp) => {
    if (formData?.alternativas?.length > 0 && (
      type === 'Matriz' ||
      type === 'Multiplos' ||
      type === 'Alternativas'
    )) {
      const getFieldName = type !== 'Matriz' ? 'valor' : 'campo';

      const hasError = formData?.alternativas?.some((alternative: { ordem: string; titulo: string; }) =>
        alternative.ordem.length === 0 ||
        alternative.titulo.length === 0 ||
        !alternative[getFieldName])

      if (hasError) {
        return `O campo não pode ser vazio.`
      }
    }

    if ((type === 'Alternativas_API' || type === 'Multiplos_API') && !formData?.alternativas) {
      return "Campo JSON vazio."
    }

    if ((type === 'Alternativas_API' || type === 'Multiplos_API') && formData?.alternativas?.length === 0) {
      return "Campo JSON vazio."
    }

    if (checkValidJsonObject(formData.alternativas)) {
      if ((type === 'Alternativas_API' || type === 'Multiplos_API') && JSON.parse(formData?.alternativas)?.length === 0) {
        return "Campo JSON inválido."
      }

      if ((type === 'Alternativas_API' || type === 'Multiplos_API') && JSON.parse(formData?.alternativas)?.length > 0) {
        const patternCheckObject = JSON.parse(formData.alternativas)?.some((alt: { name: string }) => !alt.hasOwnProperty('name'));

        if (patternCheckObject) {
          return "Campo JSON inválido."
        }
      }
    }
  }

  const handleErrors = (data: any) => {
    let errors = {};
    schema.validate(data, { abortEarly: false }).catch((err) => {
      for (let error of err.inner) {
        const { path, message } = error;
        Object.assign(
          errors, { [path]: message });
      }
      setErrorsFieldRequired(errors);
    });

    return errors;
  }

  const getFulfilledAndRejectsFields = useCallback((responses: PromiseSettledResult<AxiosResponse<any>>[]) : { 
    fulfilledField: Array<String>, 
    rejectFields: Array<String>  ,
  } => {
    const fulfilledField = responses
      .filter((res): res is PromiseFulfilledResult<AxiosResponse<any>> => res?.status === "fulfilled")
      .map((res) => res?.value?.data?.titulo as string);

    const rejectFields = addressFieldAdded.filter((field) => {
      return !fulfilledField.includes(field)
    })


    return { fulfilledField, rejectFields }
  }, [addressFieldAdded])

  const sendAddressFieldWithoutCep = useCallback(async (cepId: number) => {
    const responses = await Promise.allSettled(    
      formData?.campos_relacionados.map((field) => {
        return postField(
          field.ordem,
          field.titulo,
          field.texto_interno,
          form_slug,
          field.mascara,
          field.validador,
          field.tipo,
          field.texto_ajuda,
          field.obrigatorio,
          getAlternativas(),
          field.tipo_alternativa,
          field.limite_replicacoes,
          field.condicional_campos,
          cepId,
          field.campos_relacionados,
        );
      })
    )

    const { fulfilledField, rejectFields } = getFulfilledAndRejectsFields(responses)

      if(!!fulfilledField.length){
        setTimeout(() => {
          dispatch({
            type: actionTypes.SET_FLASH_MESSAGE,
            flashMessage: {
              show: true,
              type: 'success',
              title: 'Sucesso',
              message: `Foram salvos os campos: ${fulfilledField.join(', ')} junto com o campo CEP`,
            }
          })
        }, 2000)
      }

      if(!!rejectFields.length){
        setTimeout(() => {
          dispatch({
            type: actionTypes.SET_FLASH_MESSAGE,
            flashMessage: {
              show: true,
              type: 'error-alt',
              title: 'Error',
              message: `Não foi possivel salvar os campos: ${rejectFields.join(', ')}`,
            }
          })
        }, 3500);
      }

      handleCloseModalNewInput();
      getAllFields();
  },[addressFieldAdded])

  const populateAddressFieldsOnCep = useCallback(async () => {
    if(formData.tipo !== 'CEP') return

    try {
      const cepResponse = await  postField(
        formData.ordem,
        formData.titulo,
        formData.texto_interno,
        formData.formulario,
        formData.mascara,
        formData.validador,
        formData.tipo,
        formData.texto_ajuda,
        formData.obrigatorio,
        getAlternativas(),
        formData.tipo_alternativa,
        formData.limite_replicacoes,
        formData.condicional_campos,
        null,
        formData?.campos_relacionados,
      )

      const cepId = cepResponse?.data?.id

      if(cepId && addressFieldAdded.length > 0) {
       await sendAddressFieldWithoutCep(cepId)

      } else if(addressFieldAdded.length > 0){
        dispatch({
          type: actionTypes.SET_FLASH_MESSAGE,
          flashMessage: {
            show: true,
            type: 'error-alt',
            title: 'Error',
            message: `Não foi possivel salvar os campos: ${addressFieldAdded.join(', ')}`,
          }
        })
      }

    } catch(err){
      dispatch({
        type: actionTypes.SET_FLASH_MESSAGE,
        flashMessage: {
          show: true,
          type: 'error-alt',
          title: 'Error',
          message: `Não foi possivel salvar o campo CEP`,
        }
      })
    }
      
    
  }, [formData, form_slug, addressFieldAdded])


  const updatedAddressOnCep = useCallback(async () => {
    if(formData?.campos_relacionados?.length >= 1){
      const adressFieldsWithoutCep = formData?.campos_relacionados

      try {
        const cepReponse = await updateField(
          selectedId,
          form_slug,
          formData.ordem,
          formData.titulo,
          formData.texto_interno,
          formData.texto_ajuda,
          formData.obrigatorio,
          getAlternativas(),
          formData.tipo,
          formData.validador,
          formData.mascara,
          formData.tipo_alternativa,
          formData.limite_replicacoes,
          formData.condicional_campos,
          null,
          formData.campos_relacionados,
        )

        const cepId = cepReponse?.data?.id

        if(cepId){
          const responses = await Promise.allSettled(
            adressFieldsWithoutCep.map((field) => {
              if(field?.id){
                return updateField(
                  field.id,
                  form_slug,
                  field.ordem,
                  field.titulo,
                  field.texto_interno,
                  field.texto_ajuda,
                  field.obrigatorio,
                  getAlternativas(),
                  field.tipo,
                  field.validador,
                  field.mascara,
                  field.tipo_alternativa,
                  field.limite_replicacoes,
                  field.condicional_campos,
                  cepId,
                  field.campos_relacionados,
                )
              }

              return postField(
                field.ordem,
                field.titulo,
                field.texto_interno,
                form_slug,
                field.mascara,
                field.validador,
                field.tipo,
                field.texto_ajuda,
                field.obrigatorio,
                getAlternativas(),
                field.tipo_alternativa,
                field.limite_replicacoes,
                field.condicional_campos,
                cepId,
                field.campos_relacionados,
              );
            })
          )

         const { fulfilledField, rejectFields } = getFulfilledAndRejectsFields(responses)

         if(!!fulfilledField.length){
          setTimeout(() => {
            dispatch({
              type: actionTypes.SET_FLASH_MESSAGE,
              flashMessage: {
                show: true,
                type: 'success',
                title: 'Sucesso',
                message: `Foram atualizados os campos: ${fulfilledField.join(', ')} junto com o campo CEP`,
              }
            })
          }, 2000)
        }
  
        if(!!rejectFields.length){
          setTimeout(() => {
            dispatch({
              type: actionTypes.SET_FLASH_MESSAGE,
              flashMessage: {
                show: true,
                type: 'error-alt',
                title: 'Error',
                message: `Não foi possivel atualizar os campos: ${rejectFields.join(', ')}`,
              }
            })
          }, 3500);
        }
  
        handleCloseModalNewInput();
        getAllFields();
        }
      } catch (err){
        dispatch({
          type: actionTypes.SET_FLASH_MESSAGE,
          flashMessage: {
            show: true,
            type: 'error-alt',
            title: 'Error',
            message: `Não foi possivel atualizar o campo CEP`,
          }
        })
      }

    }
  },[formData])


  const handleAddField = async () => {
    setHasAttemptedSave(true); 
    handleErrors(formData);

    const hasErrorAlternatives = getErrorsInAlternatives(formData?.tipo as AlternativesTypeProp)

    if (typeof hasErrorAlternatives === 'string') {
      dispatch({
        type: actionTypes.SET_FLASH_MESSAGE,
        flashMessage: {
          show: true,
          type: 'error-alt',
          title: 'Error',
          message: hasErrorAlternatives,
        },
      });
      return;
    }

    if (tipoCampo === 'CEP' && formData.titulo !== 'CEP') {
      dispatch({
        type: actionTypes.SET_FLASH_MESSAGE,
        flashMessage: {
          show: true,
          type: 'error-alt',
          title: 'Error',
          message: 'Campos do tipo CEP devem ter obrigatoriamente o título "CEP"',
        },
      });
      return;
    }

    const isFormDataValid = formData.ordem &&
    formData.texto_ajuda &&
    (tipoCampo !== 'Multiplos_Arquivos' || formData.limite_replicacoes >= 2) &&
    formData.titulo &&
    (tipoCampo !== 'CEP' || formData.titulo === 'CEP') &&
    isConditionalValid;

  if (!isFormDataValid) {
    dispatch({
      type: actionTypes.SET_FLASH_MESSAGE,
      flashMessage: {
        show: true,
        type: 'error-alt',
        title: 'Error',
        message: 'Campos obrigatórios não foram preenchidos.',
      },
    });
    return;
  }


    try {
      setLoading(true);

      if (selectedId) {
        const valuesConditionalFields = formData?.condicional_campos?.length > 0
          ? formData.condicional_campos
          : []

        const wasCep = cepFormData?.id === selectedId;
        const isBecomingNonCep = wasCep && formData.tipo !== 'CEP';

        if (isBecomingNonCep && cepFormData?.campos_relacionados?.length > 0) {
          await handleRemoveAddressFields();
        }

        const { data } = await updateField(
          selectedId,
          form_slug,
          formData.ordem,
          formData.titulo,
          formData.texto_interno,
          formData.texto_ajuda,
          formData.obrigatorio,
          getAlternativas(),
          formData.tipo,
          formData.validador,
          formData.mascara,
          formData.tipo_alternativa,
          formData.limite_replicacoes,
          valuesConditionalFields,
        );
        if (data) {
          if(formData.tipo === 'CEP'){
           await updatedAddressOnCep()
          }

          handleCloseModalNewInput();
          getAllFields();
        }
        
      } else {
        if(formData.tipo === 'CEP'){
         await populateAddressFieldsOnCep()

         return 
        }

        const { data } = await postField(
          formData.ordem,
          formData.titulo,
          formData.texto_interno,
          formData.formulario,
          formData.mascara,
          formData.validador,
          formData.tipo,
          formData.texto_ajuda,
          formData.obrigatorio,
          getAlternativas(),
          formData.tipo_alternativa,
          formData.limite_replicacoes,
          formData.condicional_campos,
        );


        if (data) {
          handleCloseModalNewInput();
          getAllFields();
        }
      }

      dispatch({
        type: actionTypes.SET_FLASH_MESSAGE,
        flashMessage: {
          show: true,
          type: 'success',
          title: 'Sucesso',
          message: 'Campo salvo com sucesso',
        },
      });

    } catch (error) {
      dispatch({
        type: actionTypes.SET_FLASH_MESSAGE,
        flashMessage: {
          show: true,
          type: 'error-alt',
          title: 'Error',
          message: 'Erro ao salvar campo.',
        },
      });
      console.log(error);
    } finally {
      setLoading(false);
    }
  };


  const handleAddOption = () => {
    const newOption = {
      ordem: '',
      titulo: '',
    };
    setFormData(state => ({
      ...state,
      alternativas: [...state.alternativas, newOption]
    }));

  }


  const updateOrderInAddressFields = (orderValue: string) => {
    setFormData((state) => {
      return { 
        ...state, 
        ordem: orderValue, 
        campos_relacionados: state.campos_relacionados.map((field, fieldIdx) => {
          const cepOrder = Number(orderValue)
          if(cepOrder){
            const newOrder = Number(orderValue) + (fieldIdx + 1)

            return {
              ...field,
              ordem: newOrder.toString()
            }
          }

          return field
        })
      }
    });
  }

  const handleChange = (event: any) => {
    const { name, value } = event.target;

    if (name === 'tipo') {
      setErrorsFieldRequired(undefined);
    } else {
      errorsFieldRequired?.[name] && delete errorsFieldRequired[name];
    }

    let finalValue = name === 'limite_replicacoes' ? Number(value) : value;

    if(name === 'ordem' && formData?.titulo === 'CEP' && !!formData?.campos_relacionados.length){
      updateOrderInAddressFields(finalValue)

    } else {
      setFormData({ ...formData, [name]: finalValue });

    }


  }

  const validateConditionals = useCallback((conditional: ConditionalType) => {

    if (conditional.opcoes && conditional.opcoes.length > 0) {
      conditional.opcoes.forEach((conditionalOption) => {
        if (!conditionalOption.resposta) {
          setEmptyFields(state =>
            [...state, `conditionalOption.${conditionalOption.id}`])
        }
        if (!conditionalOption.texto_ajuda && conditionalOption.lista_campos.length > 0) {
          setEmptyFields(state =>
            [...state, `conditionalOption.helper_text.${conditionalOption.id}`])
        }

        conditionalOption.lista_campos.forEach((field) => {
          if (!field.nome) {
            setEmptyFields(state =>
              [...state, `field.nome.${field.id}`])
          }
          if (!field.texto_ajuda) {
            setEmptyFields(state =>
              [...state, `field.helper_text.${field.id}`])
          }

          field.opcoes.forEach((fieldOption) => {
            if (!fieldOption.resposta) {
              setEmptyFields(state =>
                [...state, `fieldOption.${fieldOption.id}`])
            }
          })
        })

      });
    }

  }, []);

  const validateConditionalToSubmit = () => {
    setEmptyFields([])

    if (formData.condicional_campos && formData.condicional_campos.length > 0) {

      formData.condicional_campos.forEach((conditional) => {

        // -----> Valida se os campos nome e texto de ajuda estão preenchidos antes de enviar
        //  if (!conditional.pergunta) {
        //    setEmptyFields(state => 
        //     [...state, `conditional.${conditional.id}`])

        //   }
        //  if (!conditional.texto_ajuda) {
        //    setEmptyFields(state => 
        //     [...state, `conditional.helper_text.${conditional.id}`])

        //   }
        validateConditionals(conditional);
      });
    }

    if (emptyFields.length === 0) {
      setIsConditionalValid(true)
    } else {
      setIsConditionalValid(false)
    }

  };

  const isEmptyField = (fieldName: string) => {
    const hasTheField = !!emptyFields.find(item => item === fieldName);

    return hasTheField
  }

  const removesFieldFromTheEmptyFields = ({ fieldName, removeField }: { fieldName: string; removeField?: boolean }) => {
    if (removeField) {
      setEmptyFields(state => state.filter(item => item !== fieldName))
    }
  }

  const isFieldCep = !formData?.campo && tipoCampo === 'CEP'

  useEffect(() => {
    if(isFieldCep && (!formData?.campos_relacionados || formData?.campos_relacionados?.length === 0)){
      let baseOrder = Number(formData.ordem);

      if(selectedId && formsData.length > 0) {
        const maxOrder = getMaxNumberInData<DataField>(formsData, 'ordem');
        baseOrder = maxOrder + 1;
      }

      const addressFielsWithoutCep = [
        {
          ordem: (baseOrder + 2).toString(),
          titulo: 'Estado',
          texto_interno: '',
          formulario: formData?.formulario,
          mascara: '',
          validador: '',
          tipo: 'CEP',
          texto_ajuda: 'Informe o campo estado',
          obrigatorio: true,
          alternativas: [],
          condicional_campos: [],
          limite_replicacoes: 0,
        },
          {
            ordem: (baseOrder + 1).toString(),
            titulo: 'Cidade',
            texto_interno: '',
            formulario: formData?.formulario,
            mascara: '',
            validador: '',
            tipo: 'CEP',
            texto_ajuda: 'Informe o campo cidade',
            obrigatorio: true,
            alternativas: [],
            condicional_campos: [],
            limite_replicacoes: 0,
          },
          {
            ordem: (baseOrder + 3).toString(),
            titulo: 'Bairro',
            texto_interno: '',
            formulario: formData?.formulario,
            mascara: '',
            validador: '',
            tipo: 'CEP',
            texto_ajuda: 'Informe o campo bairro',
            obrigatorio: true,
            alternativas: [],
            condicional_campos: [],
            limite_replicacoes: 0,
          },
          {
            ordem: (baseOrder + 4).toString(),
            titulo: 'Rua/Avenida',
            texto_interno: '',
            formulario: formData?.formulario,
            mascara: '',
            validador: '',
            tipo: 'CEP',
            texto_ajuda: 'Informe o campo rua/avenida',
            obrigatorio: true,
            alternativas: [],
            condicional_campos: [],
            limite_replicacoes: 0,
          }
      ]

      setFormData((state) => {
        return {
          ...state,
          ordem: baseOrder.toString(),
          titulo:  state.titulo || 'CEP',
          campos_relacionados: addressFielsWithoutCep
        }
      })
    }

    if(tipoCampo !== "CEP" && formData?.titulo !== 'CEP'){
      setFormData((state) => ({
        ...state,
        titulo: state.titulo || '',
        campos_relacionados: [] 
      }))
    }

    if(tipoCampo === "CEP" && formData?.titulo !== 'CEP'){
      setFormData((state) => ({
        ...state,
        titulo: 'CEP'
      }))
    }

  }, [selectedId, isFieldCep, tipoCampo, formData?.titulo])

  return (
    <ContainerMain>
      <Modal show={showModalNewInput} onHide={handleCloseModalNewInput} size={formData.tipo === 'Condicional' ? 'xl' : 'lg'} centered>
        <Modal.Header closeButton>
          <Modal.Title>Inserir campo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='row'>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label required>Escolha o tipo de Campo</Form.Label>
              <Form.Select placeholder='Selecione' value={tipoCampo} name='tipo' onChange={(e) => { setTipoCampo(e.target.value); handleChange(e) }}>
                <option value="Texto" selected={tipoCampo === 'Texto'}>Texto</option>
                <option value="Numero" selected={tipoCampo === 'Numero'}>Número</option>
                <option value="Data" selected={formData.tipo === 'Data'}>Data</option>
                <option value="Area" selected={formData.tipo === 'Area'}>Area</option>
                <option value="Email" selected={formData.tipo === 'Email'}>Email</option>
                <option value="Telefone" selected={formData.tipo === 'Telefone'}>Telefone</option>
                <option value="CEP" selected={formData.tipo === 'CEP'}>CEP</option>
                <option value="Arquivo" selected={formData.tipo === 'Arquivo'}>Arquivo</option>
                <option value="Alternativas" selected={formData.tipo === 'Alternativas'}>Alternativas</option>
                <option value="Alternativas_API" selected={formData.tipo === 'Alternativas_API'}>Alternativas API</option>
                <option value="Multiplos" selected={formData.tipo === 'Multiplos'}>Multiplos</option>
                <option value="Multiplos_API" selected={formData.tipo === 'Multiplos_API'}>Multiplos API</option>
                <option value="Multiplos_Arquivos" selected={formData.tipo === 'Multiplos_Arquivos'}>Multiplos Arquivos</option>
                <option value="Matriz" selected={formData.tipo === 'Matriz'}>Matriz</option>
                <option value="Condicional" selected={formData.tipo === 'Condicional'}>Condicional</option>
              </Form.Select >
              <Form.Text className="text-muted">
                Selecione o tipo de campo adequado para a informação a ser inserida pelo usuário
              </Form.Text>
            </Form.Group >

            <Form.Group className="mb-3 col-md-6">
              <FormLabelCustom required>Ordem</FormLabelCustom>
              <Form.Control placeholder="Ordem" name='ordem'
                isInvalid={errorsFieldRequired && errorsFieldRequired?.ordem}
                required value={formData.ordem} onChange={handleChange} />
              {errorsFieldRequired && errorsFieldRequired?.ordem && <Form.Control.Feedback type="invalid">
                Campo obrigatório
              </Form.Control.Feedback>}
              <Form.Text className="text-muted">
                Informe qual a ordem de exibição do campo no formulário
              </Form.Text>
            </Form.Group>

          </div >

          <div className='row'>
            <Form.Group className="mb-3 col-md-8">
              <FormLabelCustom required>Título</FormLabelCustom>
              <Form.Control placeholder="Título"
                isInvalid={errorsFieldRequired && errorsFieldRequired?.titulo}
                required name='titulo' value={formData.titulo} onChange={handleChange} disabled={tipoCampo === 'CEP'} />
              {errorsFieldRequired && errorsFieldRequired?.titulo && <Form.Control.Feedback type="invalid">
                Campo obrigatório
              </Form.Control.Feedback>}
              <Form.Text className="text-muted">
                Informe um título adequado para o campo
              </Form.Text>
              {tipoCampo === 'CEP' && (
                <Form.Text className="text-info d-block mt-1">
                  Campos do tipo CEP devem ter obrigatoriamente o título "CEP"
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group className="mb-3 col-md-4">
              <Form.Label>Obrigatório</Form.Label>
              <Form.Select placeholder='Selecione' name='obrigatorio' onChange={(e) => {
                setFormData({ ...formData, obrigatorio: e.target.value === 'true' })
              }}>
                <option value="true" selected={formData.obrigatorio}>Sim</option>
                <option value="false" selected={!formData.obrigatorio}>Não</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Informe se o campo terá preenchimento obrigatório ou não
              </Form.Text>
            </Form.Group>
          </div>

          <div className='row'>
            <Form.Group className="mb-3 col-md-6">
              <FormLabelCustom required>Texto de Ajuda</FormLabelCustom>
              <Form.Control placeholder="Texto de Ajuda" name='texto_ajuda'
                isInvalid={errorsFieldRequired && errorsFieldRequired?.texto_ajuda}
                required value={formData.texto_ajuda} onChange={handleChange} />
              {errorsFieldRequired && errorsFieldRequired?.texto_ajuda && <Form.Control.Feedback type="invalid">
                Campo obrigatório
              </Form.Control.Feedback>}
              <Form.Text className="text-muted">
                Essa informação irá orientar o usuário a preencher corretamente o campo
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Texto interno</Form.Label>
              <Form.Control placeholder="Texto interno" name='texto_interno' required value={formData.texto_interno} onChange={handleChange} />
              <Form.Text className="text-muted">
                Insira uma orientação de preenchimento do campo
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Máscara</Form.Label>
              <Form.Control placeholder="Máscara" name='mascara' required value={formData.mascara} onChange={handleChange} />
              <Form.Text className="text-muted">
                A máscara irá definir o formato do conteúdo a ser digitado no campo
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Validador</Form.Label>
              <Form.Control placeholder="Validador" name='validador' required value={formData.validador} onChange={handleChange} />
              <Form.Text className="text-muted">
                Informe uma expressão regular para validar o preenchimento do campo
              </Form.Text>
            </Form.Group>
          </div>
          
          {formData.tipo === 'Multiplos_Arquivos' && (
            <div>
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Limite de Arquivos</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Limite de Arquivos"
                  name='limite_replicacoes'
                  required={tipoCampo === 'Multiplos_Arquivos'}
                  value={formData.limite_replicacoes?.toString() || '0'}
                  onChange={(event) => {
                    const rgx = /^[0-9\b]+$/;
                    const value = event.currentTarget.value;

                    if (value === '' || rgx.test(value)) {
                      handleChange({
                        ...event,
                        currentTarget: {
                          ...event.currentTarget,
                          value: value === '' ? '' : parseInt(value, 10)
                        }
                      });
                    }
                  }}
                  isInvalid={hasAttemptedSave && formData.tipo === 'Multiplos_Arquivos' && formData.limite_replicacoes < 2}
                />
                {hasAttemptedSave && formData.tipo === 'Multiplos_Arquivos' && formData.limite_replicacoes < 2 && (
                  <Form.Control.Feedback type="invalid">
                    O número deve ser maior ou igual a 2
                  </Form.Control.Feedback>
                )}
                <Form.Text className="text-muted">
                  Informe o limite de arquivos permitidos.
                </Form.Text>
              </Form.Group>
            </div>
          )}

          {formData.tipo === 'Matriz' && <div>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label>Limite de Replicações</Form.Label>
              <Form.Control
                type="number"
                placeholder="Limite de Replicações"
                name='limite_replicacoes'
                required
                value={formData.limite_replicacoes}
                onChange={handleChange}
              />
              <Form.Text className="text-muted">
                Informe o limite de replicações permitido.
              </Form.Text>
            </Form.Group>
          </div>}

          {
            showOptionsTwo && (<>
              {(formData.tipo === 'Alternativas') && (<>
                <Form.Group className="mb-3 col-md-12">
                  <Form.Label>Tipo de Alternativa</Form.Label>
                  <Form.Select placeholder='Selecione' name='tipo_alternativa'
                    onChange={(e) => {
                      setFormData({ ...formData, tipo_alternativa: e.target.value })
                    }}
                    value={formData.tipo_alternativa}
                  >
                    <option value="Select">Select</option>
                    <option value="RadioButton">RadioButton</option>
                  </Form.Select>
                </Form.Group></>)}

              {(formData.tipo === 'Alternativas_API' || formData.tipo === 'Multiplos_API') && (<>
                <div className='row'>
                  <Form.Group className="mb-3 col-md-12">
                    <Form.Label>Adicionar dados (JSON)</Form.Label>
                    <Alert variant='secondary'>
                      <p>Exemplo a ser seguido: </p>
                      <code>
                        {JSON.stringify([{ name: 'valor de exemplo 1' }, { name: 'valor de exemplo 2' }])}
                      </code>
                    </Alert>
                    <Form.Control as="textarea" placeholder="Adicionar dados como JSON" style={{ height: '300px' }} name='alternativas'
                      defaultValue={JSON.stringify(formData.alternativas)}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </div></>)}

              {formData.tipo === 'Condicional' && (
                <Conditional
                  formData={formData}
                  setFormData={setFormData}
                  isEmptyField={isEmptyField}
                  removesFieldFromTheEmptyFields={removesFieldFromTheEmptyFields}
                />
              )}
              {(formData.tipo === 'Alternativas' || formData.tipo === 'Multiplos' || formData.tipo === 'Matriz') && (<>
                <div className='row'>
                  <div className="fw-bold">Lista de {formData.tipo === 'Matriz' ? 'Campos' : 'Alternativas'}</div>
                </div>
                {formData.alternativas.map((alternativa, index) => (
                  <div className='row' key={index.toString()}>
                    <Form.Group className="mb-3 col-md-1">
                      <Form.Label>Ordem</Form.Label>
                      <Form.Control placeholder="Ordem" readOnly value={index + 1} />
                    </Form.Group>
                    <Form.Group className="mb-3 col-md-4">
                      <FormLabelCustom required>Titulo</FormLabelCustom>
                      <Form.Control placeholder="Titulo" value={alternativa.titulo} onChange={(e) => {
                        const getOldOrderData = formData.alternativas.filter((alt: AlternativesProp) => alt.ordem !== formData.alternativas[index].ordem)
                        const nextOrderNumber = getOldOrderData?.length > 0 ? getMaxNumberInData<AlternativesProp[]>(getOldOrderData, 'ordem') : 0;
                        formData.alternativas[index].titulo = e.target.value;
                        formData.alternativas[index].ordem = (Number(nextOrderNumber) + 1).toString();
                        setFormData({
                          ...formData
                        })
                      }} />
                    </Form.Group>
                    {formData.tipo !== 'Matriz' && formData.tipo !== 'Condicional' ?
                      (
                        <Form.Group className="mb-3 col-md-5">
                          <FormLabelCustom required>Valor</FormLabelCustom>
                          <Form.Control placeholder="Titulo" value={alternativa.valor} onChange={(e) => {
                            formData.alternativas[index].valor = e.target.value;
                            setFormData({ ...formData })
                          }} />
                        </Form.Group>)
                      : (
                        <Form.Group className="mb-3 col-md-5">
                          <FormLabelCustom required>Escolha o tipo de Campo</FormLabelCustom>
                          <Form.Select placeholder='Selecione' value={alternativa.campo} name='tipo' onChange={(e) => {
                            formData.alternativas[index].campo = e.target.value;
                            setFormData({ ...formData })
                          }}>
                            <option value="none" selected disabled>Selecione</option>
                            <option value="Texto" selected={tipoCampo === 'Texto'}>Texto</option>
                            <option value="Numero" selected={tipoCampo === 'Numero'}>Número</option>
                            <option value="Data" selected={tipoCampo === 'Data'}>Data</option>
                            <option value="Area" selected={tipoCampo === 'Area'}>Area</option>
                            <option value="Email" selected={tipoCampo === 'Email'}>Email</option>
                            <option value="Telefone" selected={tipoCampo === 'Telefone'}>Telefone</option>
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Selecione o tipo de campo adequado para a informação a ser inserida pelo usuário
                          </Form.Text>
                        </Form.Group>
                      )
                    }
                    <div className='col-md-2 mt-4 pt-2'>
                      <Button variant="text" onClick={() => {
                        const alternativas = formData.alternativas;
                        alternativas.splice(index, 1);
                        setFormData({ ...formData, alternativas })
                      }}><FaTrash /></Button>
                    </div>
                  </div>
                ))}
                <Button onClick={handleAddOption}>Adicionar</Button>
              </>)}
            </>)
          }
          {(isFieldCep && formData?.campos_relacionados?.length > 0) && (
            <CustomAlert
            variant="primary"
            >
              <InfoIcon />
              {!selectedId
                ? `Ao adicionar o campo CEP, os campos: ${addressFieldAdded.join(', ')} serão gerados automaticamente.`
                : `Ao converter para CEP, os campos: ${addressFieldAdded.join(', ')} serão gerados automaticamente.`
              }
            </CustomAlert>
          )}
          {isFieldCep && (
            <AddressList
              selectedId={selectedId}
              formData={formData}
              setFormData={setFormData}
              getAllFields={getAllFields}
              formsData={formsData}
            />
          )}
        </Modal.Body >
        <Modal.Footer onMouseOver={validateConditionalToSubmit}>
          {loading 
            ? <Spinner animation="border" variant="primary" /> 
            : 
              <>
                <Button variant="danger" onClick={handleCloseModalNewInput}>
                  Cancelar
                </Button>
                <Button variant="primary" onClick={handleAddField}>
                  Salvar
                </Button>
              </>
          }
        </Modal.Footer>
      </Modal >
      <Card>
        <CardHeader>
          <Box>
            <CardIcon>
              <MdSettings size={20} />
            </CardIcon>
          </Box>
          <Title>
            Gerenciar Campos
          </Title>
        </CardHeader>
        <Card.Body>
          <ButtonHeader
            onClick={() => {
              setSelectedId(undefined);
              handleShowModalNewInput()
            }}>
            Incluir Campo
          </ButtonHeader>
          <ListGroup>
            {formsData
            .filter((field) => {
              if(field.titulo === 'CEP'){
                return true
              }

              if(field.tipo === 'CEP' && !field.campo){
                return true
              }

              return field.tipo !== 'CEP'
            })
            .map((form, index) => (
              <ListGroup.Item key={index.toString()}
                className="d-flex justify-content-between align-items-start"> <div className="ms-2 me-auto">
                  <div className="fw-bold">{form.tipo}</div>
                  {form.ordem} - {form.titulo} - {form.obrigatorio ? 'Campo obrigatório' : ''}
                </div>
                <div>
                  <Button variant="primary m-2" onClick={() => {
                    setSelectedId(form.id);
                    setTipoCampo(form.tipo);
                    setFormData(form)
                    setShowModalNewInput(true);
                  }}>Detalhes</Button>
                  <Button variant="danger" onClick={() => {
                    setSelectedId(form.id);
                    setShowModalCancel(true);
                  }}>Remover</Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
          {loading && <Spinner animation="border" variant="primary" />}

        </Card.Body>
        <Box style={{ display: 'flex', marginLeft: 22, marginBottom: 46 }}>

          <Button
            className="button"
            style={{ backgroundColor: '#EC4C47', border: 0, display: 'flex', alignItems: 'center' }}
            onClick={() => history.push(`/informacoes/${flow_slug}`)}>
            <RiCloseCircleFill size={20} />
            <span style={{ marginLeft: 8 }}>
              Fechar
            </span>
          </Button>
        </Box>
      </Card>
      <CancelModal
        show={showModalCancel}
        handleShow={setShowModalCancel}
        title="Remover Campo"
        description='Tem certeza que deseja remover o campo?'
        handleActionOk={() => handleRemove(selectedId)}
        loading={loading}
      />
    </ContainerMain >
  );
}
export default Build;


